import React, { useEffect, useMemo, useRef, useState } from 'react';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import useSelectedRadio from '../hooks/useSelectedRadio';

function normalizeValidationDetails(details) {
  if (!details) return [];

  if (Array.isArray(details)) {
    return details.map((item) => {
      if (typeof item === 'string') {
        return { path: '', message: item };
      }

      const path = Array.isArray(item?.path)
        ? item.path.join('.')
        : Array.isArray(item?.loc)
          ? item.loc.join('.')
          : item?.path || item?.field || '';

      return {
        path,
        message: item?.message || item?.msg || item?.error || JSON.stringify(item),
      };
    });
  }

  if (typeof details === 'object') {
    return Object.entries(details).map(([path, message]) => ({
      path,
      message: typeof message === 'string' ? message : JSON.stringify(message),
    }));
  }

  return [{ path: '', message: String(details) }];
}

function getBackendCode(data) {
  if (!data || typeof data !== 'object') return '';
  const detail = data.detail;
  if (detail && typeof detail === 'object') {
    return detail.code || detail.error || detail.type || '';
  }
  return data.code || data.error || data.type || '';
}

function getBackendDetails(data) {
  if (!data || typeof data !== 'object') return null;
  if (data.details) return data.details;
  if (data.detail && typeof data.detail === 'object') {
    return data.detail.details || data.detail.errors || null;
  }
  return null;
}

function getBackendMessage(data) {
  if (!data) return '';
  if (typeof data === 'string') return data;
  if (typeof data.detail === 'string') return data.detail;
  if (data.detail && typeof data.detail === 'object') {
    return data.detail.message || data.detail.error || '';
  }
  return data.message || data.error || '';
}

function buildBackendError(response, data) {
  const code = getBackendCode(data);

  if (code === 'schedule_date_already_imported') {
    return {
      title: 'Розклад за цю дату вже імпортовано. Повторний імпорт цієї дати заборонений.',
      details: [],
    };
  }

  if (code === 'radio_id_mismatch') {
    return {
      title: 'Цей файл не відповідає вибраній радіостанції. Імпорт зупинено.',
      details: [],
    };
  }

  if (code === 'validation_error') {
    return {
      title: 'Помилка валідації JSON.',
      details: normalizeValidationDetails(getBackendDetails(data)),
    };
  }

  if (response.status === 401) {
    return {
      title: 'Сесія завершилась. Увійдіть у кабінет повторно.',
      details: [],
    };
  }

  if (response.status === 403) {
    return {
      title: 'Доступ заборонено.',
      details: [],
    };
  }

  return {
    title: getBackendMessage(data) || `Помилка імпорту. HTTP ${response.status}`,
    details: [],
  };
}

const MONTH_NAMES_UK = [
  'Січень',
  'Лютий',
  'Березень',
  'Квітень',
  'Травень',
  'Червень',
  'Липень',
  'Серпень',
  'Вересень',
  'Жовтень',
  'Листопад',
  'Грудень',
];

const WEEKDAY_LABELS_UK = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'];

function pad2(value) {
  return String(value).padStart(2, '0');
}

function dateKeyUtc(year, monthIndex, day) {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
}

function buildCalendarDays(year, monthIndex) {
  const firstDay = new Date(Date.UTC(year, monthIndex, 1));
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const leadingEmptyDays = (firstDay.getUTCDay() + 6) % 7;

  return [
    ...Array.from({ length: leadingEmptyDays }, () => null),
    ...Array.from({ length: daysInMonth }, (_, index) => index + 1),
  ];
}

function shiftCalendarMonth(year, monthIndex, direction) {
  const next = new Date(Date.UTC(year, monthIndex + direction, 1));

  return {
    year: next.getUTCFullYear(),
    monthIndex: next.getUTCMonth(),
  };
}

export default function ScheduleImportPage() {
  const { radioId, name: stationName } = useSelectedRadio();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [importedDates, setImportedDates] = useState([]);
  const [importedDatesLoading, setImportedDatesLoading] = useState(false);
  const [importedDatesError, setImportedDatesError] = useState('');
  const [importedDatesRefreshKey, setImportedDatesRefreshKey] = useState(0);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();

    return {
      year: now.getFullYear(),
      monthIndex: now.getMonth(),
    };
  });
  const inputRef = useRef(null);

  const API = useMemo(() => (API_BASE_URL || '').replace(/\/+$/, ''), []);
  const canSubmit = !loading && !!radioId && !!file;

  const importedDatesSet = useMemo(() => new Set(importedDates), [importedDates]);

  const calendarDays = useMemo(
    () => buildCalendarDays(calendarMonth.year, calendarMonth.monthIndex),
    [calendarMonth],
  );

  useEffect(() => {
    let cancelled = false;

    setImportedDates([]);
    setImportedDatesError('');

    if (!radioId) {
      setImportedDatesLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setImportedDatesLoading(true);

    authFetch(`${API}/radios/${encodeURIComponent(radioId)}/schedule/imported-dates`)
      .then(async (response) => {
        const data = await response.json().catch(() => null);

        if (!response.ok || data?.ok === false) {
          throw new Error(getBackendMessage(data) || `HTTP ${response.status}`);
        }

        if (!cancelled) {
          setImportedDates(Array.isArray(data?.imported_dates) ? data.imported_dates : []);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setImportedDatesError(e?.message || 'Не вдалося завантажити календар імпортів.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setImportedDatesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [API, radioId, importedDatesRefreshKey]);

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] || null);
    setImportStatus(null);
  };

  const clearForm = () => {
    setFile(null);
    setImportStatus(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const validatePayload = (payload) => {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
      return 'JSON має бути обʼєктом верхнього рівня.';
    }

    if (!Object.prototype.hasOwnProperty.call(payload, 'radio_id')) {
      return 'У JSON відсутнє обовʼязкове поле radio_id.';
    }

    if (typeof payload.radio_id !== 'number' || !Number.isFinite(payload.radio_id)) {
      return 'radio_id має бути числом.';
    }

    if (Number(payload.radio_id) !== Number(radioId)) {
      return `Цей файл належить іншій радіостанції. Вибрана радіостанція має radio_id ${radioId}, а у файлі вказано radio_id ${payload.radio_id}. Імпорт зупинено.`;
    }

    return null;
  };

  const handleImport = async () => {
    setImportStatus(null);

    if (!file) {
      setImportStatus({ type: 'error', text: 'Оберіть JSON-файл розкладу.' });
      return;
    }

    let payload;
    try {
      const text = await file.text();
      payload = JSON.parse(text);
    } catch {
      setImportStatus({ type: 'error', text: 'Файл не є коректним JSON.' });
      return;
    }

    const validationError = validatePayload(payload);
    if (validationError) {
      setImportStatus({ type: 'error', text: validationError });
      return;
    }

    setLoading(true);
    try {
      const response = await authFetch(`${API}/radios/${encodeURIComponent(radioId)}/schedule/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || data?.ok === false) {
        const backendError = buildBackendError(response, data);
        setImportStatus({
          type: 'error',
          text: backendError.title || 'Помилка імпорту розкладу.',
        });
        return;
      }

      setImportStatus({ type: 'success', text: 'Розклад імпортовано' });

      if (data?.playlist_date) {
        setImportedDates((current) => (
          current.includes(data.playlist_date)
            ? current
            : [...current, data.playlist_date].sort()
        ));
      }

      setFile(null);
      if (inputRef.current) {
        inputRef.current.value = '';
      }

      setImportedDatesRefreshKey((current) => current + 1);
    } catch (e) {
      setImportStatus({
        type: 'error',
        text: e?.message || 'Помилка зʼєднання з сервером.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 760 }}>
      <h1 style={{ marginTop: 0 }}>Імпорт розкладу</h1>

      <p style={{ lineHeight: 1.5 }}>
        Завантажте готовий JSON-файл розкладу для вибраної радіостанції.
        Кабінет приймає тільки JSON у форматі Radiomonitor Schedule API.
        TXT, XML, CSV або інші файли тут не обробляються.
      </p>

      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: 8,
          padding: 16,
          margin: '20px 0',
          background: '#fff',
        }}
      >
        <div>
          <strong>Вибрана радіостанція:</strong>{' '}
          {stationName || (radioId ? 'Без назви' : 'Не вибрано')}
        </div>
        <div style={{ marginTop: 6 }}>
          <strong>Radiomonitor Radio ID:</strong> {radioId || '-'}
        </div>
      </div>

      {!radioId && (
        <div style={{ color: '#9a5b00', marginBottom: 16 }}>
          Оберіть радіостанцію у верхньому меню.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            ref={inputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFileChange}
          />

          {importStatus && (
            <span
              style={{
                color: importStatus.type === 'success' ? '#17633a' : '#7a2f2f',
                fontWeight: 700,
              }}
            >
              {importStatus.text}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleImport}
            disabled={!canSubmit}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: '1px solid rgb(0,110,69)',
              background: canSubmit ? 'rgb(0,110,69)' : '#ccc',
              color: '#fff',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              fontWeight: 700,
            }}
          >
            {loading ? 'Імпортуємо...' : 'Імпортувати розклад'}
          </button>

          <button
            type="button"
            onClick={clearForm}
            disabled={loading}
            style={{
              padding: '9px 16px',
              borderRadius: 8,
              border: '1px solid #bbb',
              background: '#fff',
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            Очистити
          </button>
        </div>
      </div>

      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: 8,
          padding: 16,
          margin: '20px 0',
          background: '#fff',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 14,
          }}
        >
          <button
            type="button"
            onClick={() => setCalendarMonth((current) => shiftCalendarMonth(current.year, current.monthIndex, -1))}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #bbb',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            ←
          </button>

          <strong>
            {MONTH_NAMES_UK[calendarMonth.monthIndex]} {calendarMonth.year}
          </strong>

          <button
            type="button"
            onClick={() => setCalendarMonth((current) => shiftCalendarMonth(current.year, current.monthIndex, 1))}
            style={{
              padding: '6px 10px',
              borderRadius: 6,
              border: '1px solid #bbb',
              background: '#fff',
              cursor: 'pointer',
            }}
          >
            →
          </button>
        </div>

        {importedDatesLoading && (
          <div style={{ color: '#666', marginBottom: 10 }}>
            Завантажуємо календар імпортів...
          </div>
        )}

        {importedDatesError && (
          <div style={{ color: '#8a1f1f', marginBottom: 10 }}>
            {importedDatesError}
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 6,
          }}
        >
          {WEEKDAY_LABELS_UK.map((label) => (
            <div
              key={label}
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: '#666',
                textAlign: 'center',
                padding: '4px 0',
              }}
            >
              {label}
            </div>
          ))}

          {calendarDays.map((day, index) => {
            if (!day) {
              return <div key={`empty-${index}`} />;
            }

            const dateKey = dateKeyUtc(calendarMonth.year, calendarMonth.monthIndex, day);
            const imported = importedDatesSet.has(dateKey);

            return (
              <div
                key={dateKey}
                title={imported ? `Розклад імпортовано: ${dateKey}` : `Розклад не імпортовано: ${dateKey}`}
                style={{
                  minHeight: 38,
                  borderRadius: 8,
                  border: imported ? '1px solid hsl(145 48% 48%)' : '1px solid hsl(0 54% 58%)',
                  background: imported ? 'hsl(145 52% 84%)' : 'hsl(0 58% 88%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: imported ? 'hsl(145 64% 22%)' : 'hsl(0 62% 30%)',
                  fontSize: '1.2rem',
                  fontWeight: imported ? 700 : 600,
                }}
              >
                {day}
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
          Зелені дні — розклад уже імпортовано. Червоні дні — розклад ще не імпортовано.
        </div>
      </div>
    </div>
  );
}
